import {
  flattenErrorTypeArray,
  Flex,
  SidebarDivider, SidebarTitleItem,
} from '@equifinance/shared';

import styled from 'styled-components';
import { stageOnOrAfter } from '../../contexts/helpers';
import { useAccountContext } from '../../contexts';
import { useTabNavHeight } from '../AccountTabNav';
import ProductSidebarSummary from '../Products/ProductSidebar/ProductSidebarSummary';
import ProductSidebarHeader from '../Products/ProductSidebar/ProductSidebarHeader';
import { Stages } from '../../types/Stages';
import { DeleteIcon, PlusIcon } from './components/Icons';
import useDeleteApplicant from './hooks/useDeleteApplicant';
import useAddApplicant from './hooks/useAddApplicant';
import { RentalIncomeExpenditureRefName } from './Sections/RentalIncomeExpenditure';
import { SectionSidebarItem } from '../Sidebar/SectionSidebarItem';
import { AdvisorDetailsRefName } from './Sections/AdvisorDetailsSection';
import { householdDependentsRefName } from './Sections/HouseholdDependentsSection';
import { LoanDetailsRefName } from './Sections/LoanDetails';
import { DebtManagementPlanRefName } from './Sections/DebtManagementPlan';
import { DisclosureRefName } from './Sections/DisclosureSection';
import { DirectDebitRefName } from './Sections/DirectDebitSection';
import { MortgageRefName } from './Sections/MortgageSection';
import { SecurityDetailsRefName } from './Sections/SecurityDetails';
import { MonthlyExpenditureRefName } from './Sections/MonthlyExpenditureSection';
import { IncomeRefName } from './Sections/IncomeSection';
import { collectIdFieldValues } from './utils/errors';

const ApplicantContainer = styled(Flex)`
  justify-content:space-between;
  align-items:center;
  flex-grow:1;
`;

const AddApplicantContainer = styled(Flex)`${({ theme }) => `
  color: ${theme.colours.primary[1]};
  gap: ${theme.padding[2]};
  align-items: center;
`}`;

export default function AccountSidebar() {
  const { account, creditSearchCompleted } = useAccountContext();
  const {
    stage,
    applicants = [],
    income,
    householdCompositions,
    expenditure,
    disclosure,
    directDebit,
    mortgage,
    loanDetails,
    dmpDetails,
    advisorDetails,
    rentalIncomeAndExpenditures,
    securityAddress,
    property,
    errors,
  } = account ?? {};

  const { fees } = loanDetails ?? {};
  const {
    BrokerFee, ValuationFee, AdviceFee,
  } = fees ?? {};

  const deleteApplicant = useDeleteApplicant();
  const addApplicant = useAddApplicant();

  const stickyHeaderOffset = useTabNavHeight();

  const householdCompositionErrors = errors?.[householdCompositions?.id] ?? [];

  const listChildrenUnder18Errors = householdCompositions?.listChildrenUnder18?.map(
    (childUnder18) => errors?.[childUnder18?.id],
  )?.flat()?.filter((v) => !!v) ?? [];

  const otherOccupantsErrors = householdCompositions?.otherOccupants?.map(
    (otherOccupant) => errors?.[otherOccupant?.id],
  )?.flat()?.filter((v) => !!v) ?? [];

  const combinedHouseholdCompositionsErrors = [
    ...householdCompositionErrors,
    ...listChildrenUnder18Errors,
    ...otherOccupantsErrors,
  ];

  return (
    <>
      <ProductSidebarHeader />

      <SidebarDivider />

      {applicants?.map((applicant, index) => {
        const {
          incomeAndEmployment,
          personalDetails,
          contacts,
          additionalSupportDetails,
          addresses,
        } = applicant;

        const { contactDetails = [] } = contacts ?? {};
        const contactDetailsErrors = contactDetails?.flatMap((cd) => errors?.[cd?.id] ?? []) ?? [];

        const listAddressErrors = addresses?.map(
          (address) => errors?.[address?.address?.id],
        )?.flat()?.filter((v) => !!v) ?? [];

        const listAddressesErrors = addresses?.map(
          (address) => errors?.[address?.id],
        )?.flat()?.filter((v) => !!v) ?? [];

        const { employmentHistory = [] } = incomeAndEmployment ?? {};
        const applicantWorkHistoryErrors = employmentHistory?.flatMap((eh) => [...collectIdFieldValues(eh).flatMap((id) => errors?.[id] ?? [])]) ?? [];

        const combinedErrors = flattenErrorTypeArray([
          ...(errors?.[incomeAndEmployment?.id] ?? []),
          ...(errors?.[personalDetails?.id] ?? []),
          ...(errors?.[contacts?.id] ?? []),
          ...contactDetailsErrors,
          ...(errors?.[additionalSupportDetails?.id] ?? []),
          ...(errors?.[applicant?.id] ?? []),
          ...listAddressErrors,
          ...listAddressesErrors,
          ...applicantWorkHistoryErrors,
        ]);

        return (
          <SectionSidebarItem
            stickyHeaderOffset={stickyHeaderOffset}
            key={`applicant-${applicant?.id}`}
            refName={`${applicant.id}-${index}`}
            errors={combinedErrors}
            isLocked={(stage === Stages.CreditDecision || stage === Stages.Affordability) && creditSearchCompleted}
          >
            <ApplicantContainer>
              {`Applicant ${applicants?.length > 1 ? index + 1 : ''}`}
              {(applicants?.length > 1 && !creditSearchCompleted) && (
                <DeleteIcon onClick={() => deleteApplicant(applicant, index)} />
              )}
            </ApplicantContainer>
          </SectionSidebarItem>
        );
      })}

      {(applicants?.length === 1 && !creditSearchCompleted) && (
        <SidebarTitleItem onClick={addApplicant} onScreen={false}>
          <AddApplicantContainer>
            <PlusIcon />
            Add Applicant
          </AddApplicantContainer>
        </SidebarTitleItem>
      )}

      {(stage === Stages.Affordability) && (
        <SectionSidebarItem
          stickyHeaderOffset={stickyHeaderOffset}
          refName={IncomeRefName}
          errors={flattenErrorTypeArray(errors?.[income?.id])}
        >
          Income
        </SectionSidebarItem>
      )}

      {stageOnOrAfter(stage, Stages.Affordability) && (
        <>
          <SectionSidebarItem
            stickyHeaderOffset={stickyHeaderOffset}
            refName={householdDependentsRefName}
            errors={flattenErrorTypeArray(combinedHouseholdCompositionsErrors)}
          >
            Household Dependents
          </SectionSidebarItem>

          <SectionSidebarItem
            stickyHeaderOffset={stickyHeaderOffset}
            refName={MonthlyExpenditureRefName}
            errors={flattenErrorTypeArray(errors?.[expenditure?.id])}
          >
            Monthly Expenditure
          </SectionSidebarItem>
        </>
      )}

      {stageOnOrAfter(stage, Stages.Packaging) && (
        <SectionSidebarItem
          stickyHeaderOffset={stickyHeaderOffset}
          refName={RentalIncomeExpenditureRefName}
          errors={flattenErrorTypeArray(rentalIncomeAndExpenditures?.map((rentalIncomeExpenditure) => (
            [
              ...(errors[rentalIncomeExpenditure?.id] ?? []),
              ...(errors[rentalIncomeExpenditure?.address?.id] ?? []),
            ]
          )).flat().filter((e) => !!e))}
        >
          Rental I&E
        </SectionSidebarItem>
      )}

      <SectionSidebarItem
        stickyHeaderOffset={stickyHeaderOffset}
        refName={SecurityDetailsRefName}
        errors={flattenErrorTypeArray([
          ...(errors?.[securityAddress?.id] ?? []),
          ...(errors?.[property?.id] ?? []),
        ])}
      >
        Security Details
      </SectionSidebarItem>

      {stageOnOrAfter(stage, Stages.Packaging) && (
        <>
          <SectionSidebarItem
            stickyHeaderOffset={stickyHeaderOffset}
            refName={DisclosureRefName}
            errors={flattenErrorTypeArray(errors?.[disclosure?.id])}
          >
            Disclosure
          </SectionSidebarItem>

          <SectionSidebarItem
            stickyHeaderOffset={stickyHeaderOffset}
            refName={DirectDebitRefName}
            errors={flattenErrorTypeArray(errors?.[directDebit?.id])}
          >
            Direct Debit
          </SectionSidebarItem>
        </>
      )}

      {(stage === Stages.CreditDecision) && (
        <SectionSidebarItem
          stickyHeaderOffset={stickyHeaderOffset}
          refName={MortgageRefName}
          errors={flattenErrorTypeArray(errors?.[mortgage?.id])}
          isLocked={stage === Stages.CreditDecision && creditSearchCompleted}
        >
          Mortgage
        </SectionSidebarItem>
      )}

      {!creditSearchCompleted && (
        <SectionSidebarItem
          stickyHeaderOffset={stickyHeaderOffset}
          refName={LoanDetailsRefName}
          errors={flattenErrorTypeArray([
            ...(errors?.[loanDetails?.id] ?? []),
            ...(errors?.[BrokerFee?.id] ?? []),
            ...(errors?.[ValuationFee?.id] ?? []),
            ...(errors?.[AdviceFee?.id] ?? []),
          ])}
        >
          Loan Details
        </SectionSidebarItem>
      )}

      <SectionSidebarItem
        stickyHeaderOffset={stickyHeaderOffset}
        refName={DebtManagementPlanRefName}
        errors={flattenErrorTypeArray(errors?.[dmpDetails?.id])}
      >
        Debt Management Plan
      </SectionSidebarItem>

      <SectionSidebarItem
        stickyHeaderOffset={stickyHeaderOffset}
        refName={AdvisorDetailsRefName}
        errors={flattenErrorTypeArray([
          ...(errors?.[advisorDetails?.id] ?? []),
          ...(errors?.[advisorDetails?.advisorAddress?.id ?? ''] ?? []),
        ])}
      >
        Advisor Details
      </SectionSidebarItem>

      <ProductSidebarSummary />
    </>
  );
}
